/**
 * Per-pre-order lock context.
 *
 * Provides the result of `usePreOrderLock` to descendants of
 * `PreOrderSection` (PreOrderHeader, ModifyControls, cells) so they
 * can render the locked state without each component re-aggregating
 * the underlying signals.
 *
 * Optional consumer (`usePreOrderLockOptional`) — components that may
 * render outside a section (e.g. ModifyControls under chat preview)
 * fall back to the unlocked default rather than crashing on missing
 * provider.
 */

import { createContext, useContext, type ReactNode } from "react";
import type { PreOrderLock } from "../hooks/usePreOrderLock";

const UNLOCKED: PreOrderLock = {
  isLocked: false,
  reason: null,
};

const PreOrderLockContext = createContext<PreOrderLock>(UNLOCKED);

export function PreOrderLockProvider({
  value,
  children,
}: Readonly<{ value: PreOrderLock; children: ReactNode }>) {
  return (
    <PreOrderLockContext.Provider value={value}>
      {children}
    </PreOrderLockContext.Provider>
  );
}

/** Always returns a lock — defaults to unlocked when no provider above. */
export function usePreOrderLockOptional(): PreOrderLock {
  return useContext(PreOrderLockContext);
}
